// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useState, useRef, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Shield, Heart, Cog, ChevronDown, ChevronUp } from 'lucide-react';
import { useProcessChainTree } from '@/api/queries/useProcessChains';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePermissions } from '@/hooks/usePermissions';
import { ProcessTabsNav } from '@/components/processes/ProcessTabsNav';
import {
  StatCard,
  Button,
  Skeleton,
  Page,
  PageContent,
  VisualizationFullscreenOverlay
} from '@bliks-io/ui';
import {
  VisualizationToolbar,
  VisualizationFallback
} from '@/pages/organization/systems/components/VisualizationControls';
import type { GetProcessChainTreeResponse } from '@bliks-io/contracts';

// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated

const ChainMapView = lazy(() => import('../process-map/components/ChainMapView'));

// ============================================================================
// Chain KPI Cards
// ============================================================================

function ChainKpiCards({ data }: { data: GetProcessChainTreeResponse }) {
  const { t } = useTranslation();
  const total = data.management.length + data.core.length + data.support.length;

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <StatCard
        icon={<Link2 className="w-5 h-5 text-info" />}
        title={t('processChains.title')}
        value={total}
        description=""
      />
      <StatCard
        icon={<Shield className="w-5 h-5 text-positive" />}
        title={t('processChains.categories.management')}
        value={data.management.length}
        description=""
      />
      <StatCard
        icon={<Heart className="w-5 h-5 text-info" />}
        title={t('processChains.categories.core')}
        value={data.core.length}
        description=""
      />
      <StatCard
        icon={<Cog className="w-5 h-5 text-warning" />}
        title={t('processChains.categories.support')}
        value={data.support.length}
        description=""
      />
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ProcessChainsOverviewPage() {
  const { t } = useTranslation();
  const { data: chainData, isLoading } = useProcessChainTree();
  const { can } = usePermissions();
  const canEdit = can('ProcessChain', 'update');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showKpis, setShowKpis] = useState(false);
  const resetRef = useRef<(() => void) | null>(null);

  useDocumentTitle('pageTitles.processChains');

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  const handleReset = useCallback(() => {
    resetRef.current?.();
  }, []);

  if (isLoading) {
    return (
      <Page>
        <ProcessTabsNav />
        <PageContent>
          <div className="space-y-6">
            <Skeleton className="w-64 h-8" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-lg" />
              ))}
            </div>
            <Skeleton className="w-full h-[500px] rounded-lg" />
          </div>
        </PageContent>
      </Page>
    );
  }

  return (
    <>
      <Page>
        <ProcessTabsNav />
        <PageContent className="flex flex-col overflow-hidden!">
          <div className="flex flex-col flex-1 min-h-0 gap-3">
            {/* Header */}
            <div className="flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Link2 className="w-6 h-6" />
                  {t('processChains.title')}
                </h1>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setShowKpis((v) => !v)}
                  className="text-muted-foreground"
                >
                  {showKpis ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* KPI Cards (collapsible) */}
            {showKpis && chainData && (
              <div className="shrink-0">
                <ChainKpiCards data={chainData} />
              </div>
            )}

            {/* Visualization */}
            <div className="flex-1 min-h-0 rounded-lg border bg-card shadow-xs overflow-hidden relative">
              {chainData && (
                <Suspense fallback={<VisualizationFallback height={500} />}>
                  <ChainMapView treeData={chainData} onResetRef={resetRef} canEdit={canEdit} />
                </Suspense>
              )}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                <VisualizationToolbar
                  show3DControls={false}
                  isFullscreen={false}
                  isRotating={false}
                  onToggleRotation={() => {}}
                  onReset={handleReset}
                  onToggleFullscreen={toggleFullscreen}
                />
              </div>
            </div>
          </div>
        </PageContent>
      </Page>

      {isFullscreen && chainData && (
        <VisualizationFullscreenOverlay
          icon={Link2}
          title={t('processChains.title')}
          onClose={() => setIsFullscreen(false)}
          controls={
            <VisualizationToolbar
              show3DControls={false}
              isFullscreen
              isRotating={false}
              onToggleRotation={() => {}}
              onReset={handleReset}
              onToggleFullscreen={toggleFullscreen}
            />
          }
        >
          <Suspense fallback={<VisualizationFallback height={500} />}>
            <ChainMapView treeData={chainData} onResetRef={resetRef} canEdit={canEdit} />
          </Suspense>
        </VisualizationFullscreenOverlay>
      )}
    </>
  );
}
